import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import { dateInput } from 'components/final-form/DateInput';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import { selectInput } from 'components/final-form/SelectInput';
import { textAreaInput } from 'components/final-form/TextAreaInput';
import { textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import { AnamnesisSeverityTag } from 'components/patient-entry/AnamnesisSeverityIcon';
import {
  PatientCentralConsumerRenderProps,
  withPatientCentralContext
} from 'components/patient-entry/PatientCentralContext';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import { PatientEntryConsumerRenderProps, withPatientEntryContext } from 'components/patient-entry/PatientEntryContext';
import { DataEntryValue, Label } from 'components/patient-entry/styled';
import { renderProblematicPatient } from 'components/patient-entry/upsert/PatientPane';
import { FormApi, Mutator } from 'final-form';
import createPmedDecorator from 'final-form-calculate-pmed/decorator';
import moment from 'moment';
import React, { Component, Fragment, RefObject } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, ButtonGroup, Loader, Message } from 'semantic-ui-react';
import { findDoctorById } from 'services/doctorServices';
import { getAllShippableMedicaments } from 'services/medicamentServices';
import { searchForPatientAppointment } from 'services/patientCentralServices';
import styled from 'styled-components';
import { InterventionBookingScheduleMode, InterventionExternalCode, InterventionSlotStatus } from 'ts-types/api.enums';
import {
  BookingDataDto,
  DoctorDto,
  InterventionAvailabilitySlotSearchDto,
  InterventionDto,
  MedicamentDto,
  ResourceDto,
  TreatmentBookingDto,
  UpsertPatientDto
} from 'ts-types/api.types';
import { fromIsoDateString, isoToDisplayDate, isoToDisplayTime } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { getSecondDoseVaccinationExternalCode, isFirstDoseVaccination } from 'utils/interventionExternalCodeUtils';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

const MainDiv = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin: 10px;
  overflow-y: auto;

  .hidden-field {
    display: none;
  }

  .content-pane {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    .error, .warning {
      margin-bottom: 1rem;
      margin-right: 1rem;
    }

    .data-entry {
      flex: 1 1 auto;
      display: grid;
      grid-template-columns: min-content max-content;
      justify-content: start;
      align-content: start;
      row-gap: 0.75rem;

      .label {
        min-width: 150px;
        font-weight: bold;
      }

      .value {
        max-width: 25rem;
      }

      .radio-row {
        grid-column-end: span 2;
        justify-self: start;
        margin-top: 0.5rem;
        margin-bottom: 0.3rem;
      }

      .row-label {
        margin: 1.5rem 0 1rem;
        grid-column-end: span 2;
        justify-self: start;
      }

      .search-button-row {
        margin-bottom: 0.35rem;
        grid-column-end: span 2;
      }

      .next-available-day-info {
        margin-bottom: 0.5rem;
        grid-column-start: 2;
        justify-self: start;
        font-style: italic;

        .next-date {
          display: inline-block;
          font-weight: bold;
          padding-left: 0.5rem;
        }
      }

      .available-slots-row {
        margin: 0.25rem 0 1rem;
        grid-column-start: 2;
        justify-self: start;
        max-width: 28rem;

        .time-slot {
          display: inline-block;
          margin-right: 0.5rem;
          padding: 2px 4px;
          border-radius: 4px;
          cursor: default;
          border: 1px solid #e5e5e5;
          margin-bottom: 0.5rem;
        }

        .time-slot.selected {
          background: #2185d0;
          color: white;
        }

        .time-slot.BOOKED {
          background: #f1c6dc;
          border: 1px solid #d8a1bd;
          cursor: default;
        }
      }
    }

    .slots-row {
      margin: 0.25rem 0 1rem;
      grid-column-start: 2;
      justify-self: start;
      min-width: 70rem;
    }

    .divider-row {
      grid-column-start: 1;
      grid-column-end: 3;
      margin: 0.7rem 0;
      border-bottom: 1px solid #cacaca;
    }

    .results {
      flex: 0 0 auto;
    }
  }

  .success-message {
    float: right;
    line-height: 1.2rem;
    padding: 0.5rem;
    color: #064673;
  }
`;

interface State extends IndexSignature {
  componentDidMount: boolean;
  mandates: Array<DropdownOption>;
  treatmentDetails: TreatmentDetails;
  formState?: Partial<ExtendedBookingDataDto>;
  formErrors?: boolean;
  errorMessages: string[];
  appointmentDate?: string;
  appointmentTime?: string;
  firstDoseVaccination: boolean;
  resources: ResourceDto[];
  availabilities: InterventionAvailabilitySlotSearchDto[];
  availabilityOptions: DropdownOption[];
  availabilitiesSecondDose: InterventionAvailabilitySlotSearchDto[];
  availabilitySecondDoseOptions: DropdownOption[];
  shippableMedicaments: MedicamentDto[];
  containerOptions: DropdownOption[];
  pageDataLoading: boolean,
  availableSlotsSearchWarningMessage?: string;
  availableSlotsSearchErrorMessage?: string;
  cancelTokenSource: CancelTokenSource;
}

export interface TreatmentDetails {
  date: string;
  time: string;
  tariff: string;
  reason: string;
  remarks: string;
  questionSet: string;
  anamnesisSeverity?: string;
  intervention: string;
}

interface ExtendedBookingDataDto extends BookingDataDto {
  availabilityId: number;
  interventionSlotId: number;
  secondVaccinationAvailabilityId: number;
  secondVaccinationInterventionSlotId: number;
  secondVaccinationDate: string;
  secondVaccinationTime: string;
  reminderDate: string;
  remarks: string;
}

interface Props extends PatientEntryConsumerRenderProps,
    PatientCentralConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps,
    MandateConsumerRenderProps,
    AuthConsumerRenderProps,
    MessageSourceProps {

  patient?: UpsertPatientDto,
  patientId: number,
  bookingPerformed: boolean,
  bookingSubmit: boolean,
  treatmentBooking: TreatmentBookingDto,
  onSave: (bookingData?: Partial<BookingDataDto>, errorCallback?: (exception: any) => void) => void,
  onFormUpdate: (state: Partial<BookingDataDto>) => void,
  onCancel: () => void,
  setReferralDoctor: (doctor: DoctorDto) => void,
  referralDoctor?: DoctorDto
}

const requiredForScheduleMode = (mode: InterventionBookingScheduleMode) => {
  return (value: any, allValues: any): any => {
    if (allValues.scheduleMode !== mode) {
      return undefined;
    }
    return required(value);
  };
};

class PatientAppointment extends Component<Props, State> {

  initialValues: Partial<ExtendedBookingDataDto> = {};

  form: FormApi | undefined;

  errorRef: RefObject<HTMLDivElement> = React.createRef();

  formDecorators = [
    createPmedDecorator(
        {
          field: "availabilityId",
          updateOnPristine: false,
          updates: {
            "interventionSlotId": (availabilityId, allValues) => undefined,
            "secondVaccinationAvailabilityId": (availabilityId, allValues) => {
              const availability = this.state.availabilities.find(av => av.id === availabilityId);
              if (!availability) {
                return undefined;
              }

              const secondDoseAvailability = this.state.availabilitiesSecondDose.find(
                  av => fromIsoDateString(av.date)!.diff(fromIsoDateString(availability.date), 'days') >= 28);
              return secondDoseAvailability ? secondDoseAvailability.id : undefined;
            },
            "secondVaccinationInterventionSlotId": () => undefined
          }
        }
    )
  ];

  constructor(props: Props) {
    super(props);

    const {
      treatmentBooking,
      getTariffById,
      getReasonById,
      getQuestionSetById,
      getInterventionById,
      currentMandates,
      getMessage
    } = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    const resources = this.props.resources.filter(res => res.active
        || (treatmentBooking && res.id === treatmentBooking.resourceId));

    const tariff = getTariffById(treatmentBooking.tariffId);
    const reason = getReasonById(treatmentBooking.reasonId);
    const questionSet = getQuestionSetById(treatmentBooking.questionSetId);

    const interventionId = treatmentBooking.interventionId;
    const intervention = getInterventionById(treatmentBooking.interventionId);

    const anamnesisSeverity = treatmentBooking.anamnesisSeverity
        ? getMessage(`enum.AnamnesisSeverity.${treatmentBooking.anamnesisSeverity}`)
        : "";

    const interventionDisplay = intervention
        ? `${intervention.description} - ${intervention.durationInMinutes} min`
        : "";

    const treatmentDetails: TreatmentDetails = {
      date: moment(treatmentBooking.date).format("DD.MM.YYYY"),
      time: treatmentBooking.time,
      tariff: tariff ? tariff.description : "",
      reason: reason ? reason.description : "",
      remarks: treatmentBooking.remarks,
      questionSet: questionSet ? questionSet.name : "",
      anamnesisSeverity: anamnesisSeverity,
      intervention: interventionDisplay
    }

    const containers = props.containers.filter(container => container.active && container.callCenterSupported);
    const containerOptions = mapToDropdownOptionArray(containers, "description", "id");
    containerOptions.push({
      key: "NO_CONTAINER",
      value: "NO_CONTAINER",
      text: getMessage("patientCentral.patientEntry.appointment.containerValue.NO_CONTAINER")
    });

    const externalCode = intervention!.externalCode ? InterventionExternalCode[intervention!.externalCode] : undefined;

    this.state = {
      componentDidMount: false,
      mandates: mapToDropdownOptionArray(currentMandates!, "name"),
      treatmentDetails,
      firstDoseVaccination: isFirstDoseVaccination(externalCode),
      availabilities: [],
      availabilityOptions: [],
      availabilitiesSecondDose: [],
      availabilitySecondDoseOptions: [],
      resources,
      shippableMedicaments: [],
      containerOptions: containerOptions,
      pageDataLoading: true,
      errorMessages: [],
      cancelTokenSource
    };

    const interventionDuration = intervention!.durationInMinutes;

    const bookingData = treatmentBooking.bookingData;

    const scheduleMode = bookingData && bookingData.scheduleMode
        ? bookingData.scheduleMode
        : InterventionBookingScheduleMode.APPOINTMENT;

    this.initialValues = {
      scheduleMode,
      date: moment().format('YYYY-MM-DD'),
      time: "",
      interventionSlotId: treatmentBooking.interventionSlotId,
      durationInMinutes: interventionDuration,
      reminderDate: this.getDefaultReminderDate()
    };

    if (treatmentBooking && this.props.parentBookingId) {
      this.initialValues = {
        ...this.initialValues,
        ...bookingData,
        reminderDate: bookingData.onWaitList ? bookingData.date : this.getDefaultReminderDate(),
        remarks: treatmentBooking.remarks
      };
    } else if (treatmentBooking && bookingData) {
      this.initialValues = {
        ...bookingData,
        interventionSlotId: treatmentBooking.interventionSlotId,
        reminderDate: bookingData.onWaitList ? bookingData.date : this.getDefaultReminderDate(),
        remarks: treatmentBooking.remarks
      };
    }

    this.fetchPageData(intervention);
  }

  getDefaultReminderDate() {
    return moment().add(2, 'weeks').format('YYYY-MM-DD');
  }

  componentDidMount() {
    this.setState({componentDidMount: true});
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  formUpdate = (formState: Partial<ExtendedBookingDataDto>, formErrors: boolean) => {
    this.setState({
      formState,
      formErrors
    });

    const {onFormUpdate} = this.props;
    const newFormState: Partial<BookingDataDto> = {
      ...formState
    };
    if (formState.scheduleMode === InterventionBookingScheduleMode.APPOINTMENT) {
      newFormState.onWaitList = false;
    } else if (formState.scheduleMode === InterventionBookingScheduleMode.WALK_IN) {
      const nowDateTime = moment();
      newFormState.onWaitList = false;
      newFormState.date = nowDateTime.format("YYYY-MM-DD");
      newFormState.time = nowDateTime.format("HH:mm");
    } else if (formState.scheduleMode === InterventionBookingScheduleMode.WAIT_LIST) {
      newFormState.onWaitList = true;
      newFormState.date = formState.reminderDate;
      newFormState.time = undefined;
    }
    onFormUpdate(newFormState);
  }

  fetchPageData = async (intervention: InterventionDto | undefined) => {

    const {
      treatmentBooking,
      patient,
      getInterventionById,
      getInterventionByExternalCode,
      referralDoctor,
      getMandate,
      getMessage
    } = this.props;

    this.setState({
      pageDataLoading: true,
      availableSlotsSearchWarningMessage: undefined,
      availableSlotsSearchErrorMessage: undefined
    });

    // const date = this.state.formState!.date!;

    let shippableMedicaments: MedicamentDto[];
    let availabilities: InterventionAvailabilitySlotSearchDto[];
    let availabilitiesSecondDose: InterventionAvailabilitySlotSearchDto[] = [];
    let availabilitiesResponse: InterventionAvailabilitySlotSearchDto[];
    try {
      const interventionId = this.props.treatmentBooking.interventionId;
      availabilities = await searchForPatientAppointment(
          getMandate(), interventionId, this.state.cancelTokenSource);

      const intervention = getInterventionById(interventionId);
      const externalCode = intervention!.externalCode
          ? InterventionExternalCode[intervention!.externalCode] : undefined;
      if (intervention && isFirstDoseVaccination(externalCode)) {
        const secondVaccinationExtCode = getSecondDoseVaccinationExternalCode(externalCode!);
        const secondDoseIntervention = getInterventionByExternalCode(secondVaccinationExtCode);
        availabilitiesSecondDose = await searchForPatientAppointment(
            getMandate(), secondDoseIntervention!.id, this.state.cancelTokenSource);
      }

      shippableMedicaments = await getAllShippableMedicaments(this.state.cancelTokenSource);

      if (!referralDoctor && treatmentBooking.referralDoctorId) {
        const doctor = await findDoctorById(treatmentBooking.referralDoctorId, this.state.cancelTokenSource);

        const referralDoctor: DoctorDto = {
          ...doctor,
          id: treatmentBooking.referralDoctorId
        };

        this.props.setReferralDoctor(referralDoctor);
      }

    } catch (e) {
      this.setState({
        pageDataLoading: false,
        availableSlotsSearchErrorMessage: "error"
      });
      return;
    }

    this.setState({
      shippableMedicaments
    });

    if (patient) {
      const patientAge = moment()
          .diff(patient.birthDate, 'years', false);

      availabilitiesResponse = availabilities
          .filter(availability => patientAge >= availability.minimumAge);

      availabilitiesSecondDose = availabilitiesSecondDose
          .filter(availability => patientAge >= availability.minimumAge);
    }

    if (availabilities && availabilities.length > 0) {
      availabilitiesResponse = availabilities.filter(
          av => av.resourceId === this.props.treatmentBooking.resourceId);

      const availabilityOptions = mapToDropdownOptionArrayWithTextGetter(
          availabilitiesResponse,
          this.availabilityTextGetter);

      availabilitiesSecondDose = availabilitiesSecondDose.filter(
          av => av.resourceId === this.props.treatmentBooking.resourceId);
      const availabilitySecondDoseOptions = mapToDropdownOptionArrayWithTextGetter(
          availabilitiesSecondDose,
          this.availabilityTextGetter);

      this.setState({
        availabilities,
        availabilityOptions,
        availabilitiesSecondDose,
        availabilitySecondDoseOptions
      });

      this.initialValues.availabilityId = undefined;
      if (availabilitiesResponse.length > 0) {
        const today = moment().startOf('day');
        const todayAvailability = availabilitiesResponse.find(
            av => fromIsoDateString(av.date)!.isSameOrAfter(today));
        this.initialValues.availabilityId = todayAvailability
            ? todayAvailability.id : availabilitiesResponse[0].id;
      }

      this.initialValues.scheduleMode = !availabilitiesResponse.length
          ? InterventionBookingScheduleMode.WALK_IN
          : InterventionBookingScheduleMode.APPOINTMENT

      if (this.form) {
        const interventionSlotId = this.state.formState ? this.state.formState.interventionSlotId : undefined;
        const availability = availabilitiesResponse.find(avail => {
          const foundSlot = avail.slots.find(
              slot => slot.id === interventionSlotId);
          return !!foundSlot;
        });
        if (availability) {
          this.form.mutators.setValue("availabilityId", availability.id);
          this.form.mutators.setValue("interventionSlotId", interventionSlotId);
        } else if (availabilitiesResponse.length > 0) {
          this.form.mutators.setValue("availabilityId", availabilitiesResponse[0].id);
        }

        const secondVaccinationInterventionSlotId = this.state.formState
            ? this.state.formState.secondVaccinationInterventionSlotId
            : undefined;
        const availabilitySecondDose = availabilitiesSecondDose.find(avail => {
          const foundSlot = avail.slots.find(
              slot => slot.id === secondVaccinationInterventionSlotId);
          return !!foundSlot;
        });
        if (availabilitySecondDose) {
          this.form.mutators.setValue("secondVaccinationAvailabilityId", availabilitySecondDose.id);
          this.form.mutators.setValue("secondVaccinationInterventionSlotId", secondVaccinationInterventionSlotId);
        } else if (availabilitiesSecondDose.length > 0) {
          this.form.mutators.setValue("secondVaccinationAvailabilityId", availabilitiesResponse[0].id);
        }
      }
    } else {
      const interventionExternalCode = intervention ? intervention.externalCode : "OTHER";
      this.setState({
        availableSlotsSearchWarningMessage:
            getMessage(`patientCentral.patientEntry.appointment.warning.noSlotAvailable.${interventionExternalCode}`)
      });
    }

    this.setState({
      pageDataLoading: false
    });
  }

  availabilityTextGetter = (avail: InterventionAvailabilitySlotSearchDto) =>
      `${isoToDisplayDate(avail.date)}`
      + ` (${isoToDisplayTime(avail.fromTime)} - ${isoToDisplayTime(avail.toTime)})`;

  saveTreatement = async () => {
    const {onSave} = this.props;
    const {formState, formErrors} = this.state;

    if (!formState || formErrors) {
      return;
    }

    this.clearErrors();

    onSave(undefined, this.handleError);
  }

  handleError = (exception: any) => {
    const {getMessage} = this.props;

    const error = exception!.response!.data;

    if (error) {
      const errorCode = error.errorCode;
      const knownErrors: Array<string> = [
        "INTERVENTION_BOOKING_VACCINATION_DOSES_VACCINE_MISMATCH",
        "INTERVENTION_BOOKING_VACCINATION_DOSES_TIME_MISMATCH",
        errorUtils.unmetMinimumAge
      ];

      const violations: Array<any> = error.violations;

      if (violations && violations.length > 0) {
        violations.forEach(violation => {
          if (knownErrors.includes(violation.errorCode)) {
            this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
          }
        });
      }

      if (!this.state.errorMessages.length) {
        if (knownErrors.includes(errorCode)) {
          this.setErrorMessage(getMessage(`error.${errorCode}`));
        } else {
          this.setErrorMessage(getMessage('error.general'));
        }
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages, errorMessage];

      this.setState({
        errorMessages: errMsgs
      });

      setTimeout(() => {
        this.errorRef.current && this.errorRef.current!.scrollIntoView();
      }, 50);

    } else {
      this.clearErrors();
    }
  };

  clearErrors = () => {
    this.setState({
      errorMessages: []
    });
  };

  clearWarning = () => {
    this.setState({
      availableSlotsSearchWarningMessage: undefined
    });
  };

  setValue: Mutator = ([name, newValue], state, {changeValue}) => {
    changeValue(state, name, value => newValue);
  };

  render(): React.ReactNode {

    const {
      patient,
      bookingPerformed,
      onCancel,
      bookingSubmit,
      resources,
      containers,
      products,
      referralDoctor,
      getMessage
    } = this.props;

    const {pageDataLoading, availableSlotsSearchWarningMessage, errorMessages} = this.state;

    if (pageDataLoading) {
      return <Loader active/>;
    }

    let hideTreatmentDetails = true;

    const container = containers.find(container => container.id === this.props.treatmentBooking.container);
    const containerDisplay = container ? container.description : "";

    const locationDisplay = resources.find(res => res.id === this.props.treatmentBooking.resourceId)!.description;

    const product = products.find(product => product.id === this.props.treatmentBooking.productId);
    const productDisplay = product ? product.description : "";

    return <MainDiv>
      <div className="content-pane">
        {
            errorMessages.length > 0 &&
            <div ref={this.errorRef} className="error">
              <StyledErrorMessage onDismiss={() => this.clearErrors()}>
                {errorMessages.map(err => <div key={err}>{err}</div>)}
              </StyledErrorMessage>
            </div>
        }

        {
            availableSlotsSearchWarningMessage &&
            <div className="warning">
              <Message warning onDismiss={() => this.clearWarning()}>
                {availableSlotsSearchWarningMessage}
              </Message>
            </div>
        }

        {
            patient && (
                <div className="data-entry">
                  <div className="label">{getMessage("anamnesis.form.patient")}:</div>
                  <div>
                    <div>
                      <span style={patient.doctorProblematic ? {color: "red"} : {}}>
                        {`${patient!.firstName} ${patient!.name} `}
                      </span>
                      {renderProblematicPatient(patient)}
                    </div>
                    <div>{`${moment(patient!.birthDate).format("DD.MM.YYYY")}`}</div>
                    <div>{`${patient!.street ? patient.street : ""}`}</div>
                    <div>{`${patient!.zip ? patient.zip : ""} ${patient!.city ? patient.city : ""}`}</div>
                  </div>

                  <div className="label">{getMessage("anamnesis.form.treatment.dateTime")}:</div>
                  <div className="value">
                    <div>{`${this.state.treatmentDetails.date} - ${this.state.treatmentDetails.time}`}</div>
                  </div>

                  {referralDoctor &&
                      <React.Fragment>
                        <div className="label">{getMessage("anamnesis.form.treatment.referralDoctorId")}:</div>
                        <div>
                          <div>
                            {`${referralDoctor.firstName} ${referralDoctor.name}`}&nbsp;
                            {referralDoctor.email ? `(${referralDoctor.email})` : ""}
                          </div>
                        </div>
                      </React.Fragment>
                  }

                  {!hideTreatmentDetails &&
                      <React.Fragment>
                        <div className="label">{getMessage("anamnesis.form.treatment.tariffId")}:</div>
                        <div className="value">
                          <div>{this.state.treatmentDetails.tariff}</div>
                        </div>

                        <div className="label">{getMessage("anamnesis.form.treatment.reasonId")}:</div>
                        <div className="value">
                          <div>{this.state.treatmentDetails.reason}</div>
                        </div>

                        <div className="label">{getMessage("anamnesis.form.treatment.remarks")}:</div>
                        <div className="value">
                          <div>{this.state.treatmentDetails.remarks}</div>
                        </div>
                      </React.Fragment>
                  }

                  <div className="label">{getMessage("anamnesis.form.questionSetId")}:</div>
                  <div className="value">
                    <div>{this.state.treatmentDetails.questionSet}</div>
                  </div>

                  <div className="label">{getMessage("anamnesis.form.anamnesisSeverity")}:</div>
                  <div className="value">
                    <div>
                      <AnamnesisSeverityTag severity={this.props.treatmentBooking.anamnesisSeverity}/>
                    </div>
                  </div>

                  <div className="label">{getMessage("anamnesis.form.intervention.interventionId")}:</div>
                  <div className="value">
                    <div>{this.state.treatmentDetails.intervention}</div>
                  </div>

                  <div className="label">{getMessage("patientCentral.patientEntry.appointment.container")}:</div>
                  <div className="value">
                    <div>{containerDisplay}</div>
                  </div>

                  <div className="label">{getMessage("patientCentral.patientEntry.appointment.locationId")}:</div>
                  <div className="value">
                    <div>{locationDisplay}</div>
                  </div>

                  <div className="label">{getMessage("anamnesis.form.productId")}:</div>
                  <div className="value">
                    <div>{productDisplay}</div>
                  </div>

                  <div className="divider-row"></div>

                  <FinalForm
                      onSubmit={() => {
                      }}
                      initialValues={this.initialValues}
                      decorators={this.formDecorators}
                      mutators={{setValue: this.setValue}}
                      subscription={{submitting: true, pristine: true}}
                      render={this.renderForm}
                  />

                </div>
            )
        }
      </div>

      <FormButtonsRow>
        <Button type="button" compact disabled={bookingSubmit} onClick={onCancel}>
          {getMessage("patientCentral.patientEntry.actions.back")}
        </Button>

        <Button type="button"
                compact
                primary
                floated="right"
                onClick={this.saveTreatement}
                disabled={bookingSubmit}
        >
          {getMessage("patientCentral.patientEntry.actions.finish")}
        </Button>
        {
            bookingPerformed && <div className="success-message">
              {getMessage("patientCentral.patientEntry.appointment.success")}
            </div>
        }
      </FormButtonsRow>

    </MainDiv>;
  }

  renderForm = ({handleSubmit, form}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {availabilityOptions} = this.state;
    const scheduleModes = Object.values(InterventionBookingScheduleMode);

    this.form = form;

    const values = form.getState().values;
    const searchBy = values.scheduleMode;
    // const isDifferentDate = availableSlotsDate && !availableSlotsDate.isSame(moment(values.date), "day");
    // const slotDate = isDifferentDate ? availableSlotsDate : undefined;

    return <>
      <div></div>
      <div>
        <ButtonGroup>
          {scheduleModes.map(filter => {
                let disabledAppointment = !availabilityOptions.length;
                return (
                    <Button key={filter}
                            type="button"
                            compact
                            active={searchBy === filter}
                            onClick={() => form.change("scheduleMode", filter)}
                            disabled={filter === InterventionBookingScheduleMode.APPOINTMENT && disabledAppointment}
                    >
                      {getMessage(`enum.InterventionBookingScheduleMode.${filter}`)}
                    </Button>
                )
              }
          )}
        </ButtonGroup>
      </div>

      {this.renderScheduleModeSection(form)}

      {
          this.state.firstDoseVaccination &&
          this.renderSecondDoseScheduleModeSection(form)
      }

      {
        this.mapFieldRows(
            [
              textAreaInput({
                id: "remarks",
                name: "remarks"
              }, 2)
            ],
            values,
            'patientCentral.patientEntry.appointment')
      }

      <FormSpy
          subscription={{values: true, dirtyFields: true, hasValidationErrors: true}}
          onChange={(formState) => {
            form.submit();
            this.formUpdate(formState.values as Partial<ExtendedBookingDataDto>, formState.hasValidationErrors);
          }}
      />
    </>
        ;
  }

  renderScheduleModeSection = (form: FormApi) => {
    const values = form.getState().values;
    switch (values.scheduleMode) {
      case InterventionBookingScheduleMode.APPOINTMENT:
        return <>
          {this.renderAppointmentSection(form)}

          <div className="available-slots-row">
            <div className="slots-row">
              {this.renderAvailableSlots(form)}
            </div>
          </div>
        </>;

      case InterventionBookingScheduleMode.WALK_IN:
        return <></>;

      default:
        return this.renderWaitListSection(form);
    }
  }

  renderWaitListSection = (form: FormApi): JSX.Element => {

    const values = form.getState().values;

    const treatmentFields = [
      dateInput({
        name: 'reminderDate',
        validate: requiredForScheduleMode(InterventionBookingScheduleMode.WAIT_LIST),
        disabled: values.scheduleMode !== InterventionBookingScheduleMode.WAIT_LIST
      })
    ];


    const treatmentDetails = this.mapFieldRows(
        treatmentFields, values, 'patientCentral.patientEntry.appointment');

    return (
        <>
          {treatmentDetails}
        </>
    );
  }

  renderAppointmentSection = (form: FormApi): JSX.Element => {

    const {availabilityOptions} = this.state;

    const values = form.getState().values;

    const selectMap: IndexSignature = {
      availabilityId: availabilityOptions
    };

    const treatmentFields = [

      selectInput({
        name: 'availabilityId',
        validate: requiredForScheduleMode(InterventionBookingScheduleMode.APPOINTMENT),
        disabled: values.scheduleMode !== InterventionBookingScheduleMode.APPOINTMENT
      }, selectMap),

      textInput({
        id: 'date',
        name: 'date',
        hiddenInput: true
      }, true),

      textInput({
        id: 'time',
        name: 'time',
        hiddenInput: true
      }, true),

      textInput({
        id: 'interventionSlotId',
        name: 'interventionSlotId',
        hiddenInput: true,
        validate: requiredForScheduleMode(InterventionBookingScheduleMode.APPOINTMENT)
      }, true)
    ];


    const treatmentDetails = this.mapFieldRows(
        treatmentFields, values, 'patientCentral.patientEntry.appointment');

    return (
        <>
          {treatmentDetails}
        </>
    );
  }

  mapFieldRows = (fields: Array<any>, values: any, messageKey?: string) => {

    const {getMessage} = this.props;
    const {shippableMedicaments, availabilities} = this.state;

    return fields.map((fieldProps, index) => {

      if (fieldProps === undefined) {
        return <></>;
      }

      if (fieldProps.component === ButtonGroup) {
        return <Fragment key={`booking_input_row_${index}`}>
          <DataEntryValue className="radio-row" key={`treatment_value_${index}`}>
            <Field {...fieldProps} />
          </DataEntryValue>
        </Fragment>;
      }

      let vaccineDetails = "";
      if (fieldProps.name === "availabilityId") {
        const availability = availabilities.find(a => a.id === values.availabilityId);
        if (availability) {
          const medicament = shippableMedicaments.find(m => m.id === availability.medicamentId);
          if (medicament) {
            vaccineDetails = `${medicament.description}
            (${getMessage("interventionAvailability.form.minimumAge")} ${availability.minimumAge})`;
          }
        }
      }

      const labelKey = messageKey ? `${messageKey}.${fieldProps.name}` : fieldProps.name;
      const hiddenClassName = `${fieldProps.hiddenInput ? "hidden-field" : ""}`
      return (
          <Fragment key={`booking_input_row_${index}`}>
            <Label className={`label ${hiddenClassName}`}
                   key={`treatment_label_${index}`}>{getMessage(labelKey)}:</Label>
            <DataEntryValue className={`${hiddenClassName}`} key={`treatment_value_${index}`}>
              <Field fluid {...fieldProps} />
              {fieldProps.name === "availabilityId" &&
                  <div className="availability-details">
                    {vaccineDetails}
                  </div>
              }
            </DataEntryValue>
          </Fragment>
      );
    });
  };

  renderAvailableSlots = (form: FormApi): JSX.Element => {

    const values = form.getState().values;

    const {availabilities} = this.state;

    const bookingPrev = this.props.treatmentBookingCurrent;

    const filteredAvailabilities = availabilities.filter(
        av => av.resourceId === this.props.treatmentBooking.resourceId);

    let slots: any = [];
    const availabilityId = values.availabilityId;
    if (availabilityId && filteredAvailabilities.length > 0) {

      const availability = filteredAvailabilities.find(av => av.id === availabilityId);

      if (availability) {

        let currentHour = 0;
        let currentMin = 0;

        const selectedAvailability = availabilities.find(avail => avail.id === availabilityId);

        const displayedSlots = selectedAvailability!.slots.filter(
            slot => InterventionSlotStatus.BLOCKED !== slot.status);

        slots = displayedSlots.map((slotDto, ix) => {

              let newRow = false;

              const slot = moment(slotDto.fromTime, "HH:mm");
              const slotHour = slot.hour();
              if (slotHour !== currentHour) {
                newRow = true;
                currentHour = slotHour;
              }

              const slotMin = slot.minute();
              if (currentMin !== Math.floor(slotMin / 15)) {
                newRow = true;
                currentMin = Math.floor(slotMin / 15);
              }

              const selectedInterventionSlotId = values.interventionSlotId;
              const persistedInterventionSlotId = bookingPrev ? bookingPrev.interventionSlotId : undefined;

              const selectedClassname = slotDto.id === selectedInterventionSlotId ? "selected" : "";
              const slotStatus = persistedInterventionSlotId === slotDto.id ? InterventionSlotStatus.FREE : slotDto.status;
              const bookedClassname = slotStatus ? slotStatus : "";
              return <Fragment key={ix}>
                {newRow && <div className="new-row-div"/>}
                {ix > 0 && !newRow && <> </>}
                <div
                    className={`time-slot ${selectedClassname} ${bookedClassname}`}
                    onClick={() => {
                      if (!(InterventionSlotStatus.FREE === slotStatus)) {
                        return;
                      }

                      form.mutators.setValue("interventionSlotId", slotDto.id);
                      form.mutators.setValue("date", selectedAvailability!.date);
                      form.mutators.setValue("time", slotDto.fromTime);
                    }}
                >
                  {slot.format("HH:mm")}
                </div>
              </Fragment>;
            }
        );
      }
    }

    return <>
      {slots}
    </>;
  }

  renderSecondDoseScheduleModeSection = (form: FormApi) => {

    return <>
      {this.renderSecondDoseAppointmentSection(form)}

      <div className="available-slots-row">
        <div className="slots-row">
          {this.renderSecondDoseAvailableSlots(form)}
        </div>
      </div>
    </>;
  }

  renderSecondDoseAppointmentSection = (form: FormApi): JSX.Element => {

    const values = form.getState().values;

    const selectedAvailability = this.state.availabilities.find(
        av => av.id === values.availabilityId);

    const isWalkIn = values.scheduleMode === InterventionBookingScheduleMode.WALK_IN;

    let validSecondDoseAvailabilities: InterventionAvailabilitySlotSearchDto[] = [];
    if (selectedAvailability && !isWalkIn) {
      validSecondDoseAvailabilities = this.state.availabilitiesSecondDose.filter(
          av => fromIsoDateString(av.date)!.diff(fromIsoDateString(selectedAvailability.date), 'days') >= 28);
    } else if (isWalkIn) {
      const refDate = moment();
      validSecondDoseAvailabilities = this.state.availabilitiesSecondDose.filter(
          av => fromIsoDateString(av.date)!.diff(refDate, 'days') >= 28);
    }

    const secondDoseAvailabilityOptions = mapToDropdownOptionArrayWithTextGetter(
        validSecondDoseAvailabilities, this.availabilityTextGetter);

    const selectMap: IndexSignature = {
      secondVaccinationAvailabilityId: secondDoseAvailabilityOptions
    };

    const treatmentFields = [

      selectInput({
        name: 'secondVaccinationAvailabilityId',
        validate: required
      }, selectMap),

      textInput({
        id: 'secondVaccinationInterventionSlotId',
        name: 'secondVaccinationInterventionSlotId',
        hiddenInput: true,
        validate: this.minDaysBetweenVaccinations
      }, true)
    ];

    const treatmentDetails = this.mapFieldRows(
        treatmentFields, values, 'patientCentral.patientEntry.appointment');

    return (
        <>
          {treatmentDetails}
        </>
    );
  }

  renderSecondDoseAvailableSlots = (form: FormApi): JSX.Element => {

    const values = form.getState().values;

    const availabilities = this.state.availabilitiesSecondDose;

    const bookingPrev = this.props.treatmentBookingCurrent;

    const filteredAvailabilities = availabilities.filter(
        av => av.resourceId === this.props.treatmentBooking.resourceId);

    let slots: any = [];
    const availabilityId = values.secondVaccinationAvailabilityId;
    if (availabilityId && filteredAvailabilities.length > 0) {

      const availability = filteredAvailabilities.find(av => av.id === availabilityId);

      if (availability) {

        let currentHour = 0;
        let currentMin = 0;

        const selectedAvailability = availabilities.find(avail => avail.id === availabilityId);

        const displayedSlots = selectedAvailability!.slots.filter(
            slot => InterventionSlotStatus.BLOCKED !== slot.status);

        slots = displayedSlots.map((slotDto, ix) => {

              let newRow = false;

              const slot = moment(slotDto.fromTime, "HH:mm");
              const slotHour = slot.hour();
              if (slotHour !== currentHour) {
                newRow = true;
                currentHour = slotHour;
              }

              const slotMin = slot.minute();
              if (currentMin !== Math.floor(slotMin / 15)) {
                newRow = true;
                currentMin = Math.floor(slotMin / 15);
              }

              const selectedInterventionSlotId = values.secondVaccinationInterventionSlotId;
              const persistedInterventionSlotId =
                  bookingPrev ? bookingPrev.secondVaccinationInterventionSlotId : undefined;

              const selectedClassname = slotDto.id === selectedInterventionSlotId ? "selected" : "";
              const slotStatus = persistedInterventionSlotId === slotDto.id ? InterventionSlotStatus.FREE : slotDto.status;
              const bookedClassname = slotStatus ? slotStatus : "";
              return <Fragment key={ix}>
                {newRow && <div className="new-row-div"/>}
                {ix > 0 && !newRow && <> </>}
                <div
                    className={`time-slot ${selectedClassname} ${bookedClassname}`}
                    onClick={() => {
                      if (!(InterventionSlotStatus.FREE === slotStatus)) {
                        return;
                      }

                      form.mutators.setValue("secondVaccinationInterventionSlotId", slotDto.id);
                      form.mutators.setValue("secondVaccinationDate", selectedAvailability!.date);
                      form.mutators.setValue("secondVaccinationTime", slotDto.fromTime);
                    }}
                >
                  {slot.format("HH:mm")}
                </div>
              </Fragment>;
            }
        );
      }
    }

    return <>
      {slots}
    </>;
  }

  minDaysBetweenVaccinations = (value: any, allValues: any): any => {

    const requiredResult = required(value);
    if (requiredResult) {
      return requiredResult;
    }

    if (!allValues.secondVaccinationInterventionSlotId
        || !allValues.secondVaccinationDate
        || !allValues.secondVaccinationTime) {
      return undefined;
    }

    return fromIsoDateString(allValues.secondVaccinationDate)!.diff(fromIsoDateString(allValues.date), 'days') < 28
        ? "minDaysBetweenVaccinations" : undefined;
  };

}

export default withMessages(
    withAuthContext(
        withMandateContext(
            withPatientCentralRefDataContext(
                withPatientCentralContext(
                    withPatientEntryContext(PatientAppointment))))));
